import { z } from 'zod';
/**
 * Who performed an action, as the public API reports it (KIR-4376 / KIR-3747).
 *
 * This is the contract-level statement of the identity model KIR-3747 settled
 * on, and it is deliberately **not** a synthetic service-account user:
 *
 * - A management API key never gets an identity of its own. It acts as the
 *   person who minted it, so `userId` / `name` / `email` are always that human
 *   — the same `AccessKeyAuthContext.userId` the write was attributed to, and
 *   the same value `audit_logs.user_id` carries.
 * - The key is reported *alongside* that human, in `apiKey`. It answers "which
 *   credential", never "who".
 *
 * The alternative — minting a service-account user per key — was rejected in
 * KIR-3747: it would put a non-person in `audit_logs.user_id`, break the
 * `user_id` foreign key's meaning, and lose the accountability trail back to a
 * real employee. Consumers that want "was this a human or an integration?"
 * read {@link V1ActorType}; consumers that want "who is answerable for it?"
 * read `userId`. Both questions stay answerable, which a service account would
 * not have allowed.
 */
/**
 * How an action reached the system.
 *
 * `apiKey` does not mean "no human was involved" — see the module doc. It means
 * a management API key carried the request, and `apiKey` names which one.
 */
export var V1ActorType;
(function (V1ActorType) {
    /** A signed-in person acting through the Kirin app. */
    V1ActorType["User"] = "user";
    /** A management API key, acting as the person named by `userId`. */
    V1ActorType["ApiKey"] = "apiKey";
    /**
     * No attributable actor: a database trigger, a background job, or a row
     * whose user was deleted. `userId` is null.
     */
    V1ActorType["System"] = "system";
})(V1ActorType || (V1ActorType = {}));
export const v1ActorTypeSchema = z.enum(V1ActorType);
/**
 * The credential half of an actor. `id` is the Descope access-key id (stable
 * for the life of the key); `name` is the label the customer gave it when
 * minting, and is null for a key whose mirror row was created without one.
 */
export const v1ApiKeyRefSchema = z
    .object({
    id: z
        .string()
        .describe('Key id, stable for the life of the key. Matches `apiKeyId` from the health endpoint.'),
    name: z
        .string()
        .nullable()
        .describe('The label given to the key when it was created. Null if it was created without one.'),
})
    .meta({
    id: 'V1ApiKeyRef',
    title: 'API key reference',
    description: 'Which credential carried a request. It answers "which key", never "who" — see `V1Actor`.',
});
export const v1ActorSchema = z
    .object({
    type: v1ActorTypeSchema.describe('How the action reached the system. `apiKey` does not mean no human was involved — it means a management API key carried the request, acting as the person in `userId`.'),
    /**
     * Kirin user id of the person answerable for the action — the key's creator
     * when `type` is `apiKey`. Null only for `system`.
     */
    userId: z
        .string()
        .nullable()
        .describe("Kirin user id of the person answerable for the action — the key's creator when `type` is `apiKey`. Null only for `system`."),
    name: z
        .string()
        .nullable()
        .describe("That person's display name, if known."),
    email: z
        .string()
        .nullable()
        .describe("That person's email address, if known."),
    /** Present only when `type` is `apiKey`; null for every other actor type. */
    apiKey: v1ApiKeyRefSchema
        .nullable()
        .describe('The key that carried the request. Non-null only when `type` is `apiKey`.'),
})
    .meta({
    id: 'V1Actor',
    title: 'Actor',
    description: 'Who performed an action. A management API key has no identity of its own: it acts as the person who created it, so `userId` is always a real person and `apiKey` names the credential separately. Read `type` for "human or integration?"; read `userId` for "who is answerable?".',
    examples: [
        {
            type: 'apiKey',
            userId: 'u_2Nk8QpXyZ',
            name: 'Dana Okafor',
            email: 'dana@example.com',
            apiKey: { id: 'K2b9c1d4e6f8a0', name: 'Splunk connector' },
        },
    ],
});
/**
 * A person referenced from somewhere other than the actor position — the users
 * an alert was seen for, whoever muted it.
 *
 * `id` is the Kirin user id, which is also the value the `userId` list filter
 * accepts. That correspondence is the point: an id you read from a response can
 * be fed straight back into a query.
 */
export const v1UserRefSchema = z
    .object({
    id: z
        .string()
        .describe('Kirin user id. This is the same value the `userId` list filter accepts, so an id read from a response can be fed straight back into a query.'),
    name: z.string().nullable().describe('Display name, if known.'),
    email: z.string().nullable().describe('Email address, if known.'),
})
    .meta({
    id: 'V1UserRef',
    title: 'User reference',
    description: 'A person referenced from somewhere other than the actor position — the users an alert was seen for, whoever muted it.',
});
//# sourceMappingURL=actor.js.map